import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { debounceTime, distinctUntilChanged } from 'rxjs';

import { backendEndPointToken, serviceRequestCategoriesToken } from '../../app.config';
import { ErrorService } from '../error.service';
import { UtilityService, ServiceRequestCodeOptionWithStatus } from '../utility.service';

export type ServiceRequestCategoryOption = { code: string; display: string; system: string };

export type ServiceRequestSelection = {
    category: ServiceRequestCategoryOption | null;
    serviceRequestCode: ServiceRequestCodeOptionWithStatus | string | null;
};

@Component({
    selector: 'app-service-request-code-picker',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        MatAutocompleteModule,
        MatIconModule
    ],
    templateUrl: './service-request-code-picker.component.html',
    styleUrl: './service-request-code-picker.component.scss'
})
export class ServiceRequestCodePickerComponent implements OnInit {
    private fb = inject(FormBuilder);
    private utilityService = inject(UtilityService);
    private errorService = inject(ErrorService);

    // Ensures UtilityService gets instantiated with correct tokens (side-effect: cached CodeSystem fetch uses these).
    // Not directly used here, but keeps the component future-proof if token wiring changes.
    private backendUrl = inject(backendEndPointToken);

    private serviceCategories = inject(serviceRequestCategoriesToken);

    /**
     * If provided, the component will attempt to preselect the matching category
     * and disable the category picker (like the current dialog behavior).
     */
    @Input() typeOfService: string | null = null;

    /** Disable the entire picker UI. */
    @Input() disabled = false;
    @Input() mustChooseFromOptions: boolean = false;

    /**
     * When true (default), service codes won't be fetched until a category is selected.
     * Set to false if you want global search across all categories.
     */
    @Input() requireCategory = true;

    @Output() selectionChange = new EventEmitter<ServiceRequestSelection>();

    categoryForm = this.fb.group({
        category: ['', Validators.required],
        serviceRequestCode: ['', Validators.required]
    });

    categoryOptions: ServiceRequestCategoryOption[] = [];
    filteredCategoryOptions: ServiceRequestCategoryOption[] = [];

    serviceRequestOptions: ServiceRequestCodeOptionWithStatus[] = [];
    filteredServiceRequestOptions: ServiceRequestCodeOptionWithStatus[] = [];

    loadingServiceRequests = false;

    selectedCategory: string | null = null;

    ngOnChanges(changes: SimpleChanges): void {
        const normalized = String(this.typeOfService).trim().toLowerCase();
        const source = this.categoryOptions?.length ? this.categoryOptions : [...this.serviceCategories];

        // Refresh options if caller requests it.
        if (changes['typeOfService'] && !changes['typeOfService'].firstChange) {
            // this.utilityService.invalidateServiceRequestCodeCaches();
            // // Keep current category selection; just reload available
            //  codes.
            // this.searchServiceRequestCode('');
            this.typeOfService = changes['typeOfService'].currentValue ? String(changes['typeOfService'].currentValue) : null;


            const categoryControl = this.categoryForm.get('category');
            const serviceCodeControl = this.categoryForm.get('serviceRequestCode');


            if (!categoryControl || !serviceCodeControl) return;

            // If typeOfService is removed, restore normal category selection (unless entire form is disabled).
            if (!this.typeOfService) {
                categoryControl.setValue('' as any, { emitEvent: false });



                // if (!this.disabled) {
                //     categoryControl.enable({ emitEvent: false });
                // }

                // this.serviceRequestOptions = [];
                // this.filteredServiceRequestOptions = [];
                // serviceCodeControl.setValue('', { emitEvent: false });

                // this.emitSelection();
                // this.selectedCategory = sC;
                const match = source.find((c) =>
                    String(c?.code ?? '').toLowerCase() === this.selectedCategory?.toLowerCase().trim() || String(c?.display ?? '').toLowerCase() === this.selectedCategory?.toLowerCase().trim()
                );
                categoryControl.setValue(match as any, { emitEvent: false });


                this.serviceRequestOptions = [];
                this.filteredServiceRequestOptions = [];
                serviceCodeControl.setValue('', { emitEvent: false });

                this.searchServiceRequestCode('');
                this.emitSelection();
                return;
            }


            const match = source.find((c) =>
                String(c?.code ?? '').toLowerCase() === normalized || String(c?.display ?? '').toLowerCase() === normalized
            );

            if (!match) {
                // If we can't match the new typeOfService, don't force a category.
                this.selectedCategory = null;

                if (!this.disabled) {
                    categoryControl.enable({ emitEvent: false });
                }

                this.serviceRequestOptions = [];
                this.filteredServiceRequestOptions = [];
                serviceCodeControl.setValue('', { emitEvent: false });

                this.emitSelection();
                return;
            }

            // Apply new forced category.
            this.selectedCategory = match.code;
            categoryControl.setValue(match as any, { emitEvent: false });
            categoryControl.disable({ emitEvent: false });

            this.serviceRequestOptions = [];
            this.filteredServiceRequestOptions = [];
            serviceCodeControl.setValue('', { emitEvent: false });

            this.searchServiceRequestCode('');
            this.emitSelection();
        }
    }

    ngOnInit(): void {
        this.categoryOptions = [...this.serviceCategories];
        this.filteredCategoryOptions = [...this.serviceCategories];

        if (this.disabled) {
            this.categoryForm.disable({ emitEvent: false });
        }

        // Preselect category if typeOfService is supplied.
        if (this.typeOfService) {
            const normalized = String(this.typeOfService).trim().toLowerCase();
            const match = this.categoryOptions.find((c) =>
                String(c?.code ?? '').toLowerCase() === normalized || String(c?.display ?? '').toLowerCase() === normalized
            );

            if (match) {
                this.selectedCategory = match.code;
                this.categoryForm.get('category')?.setValue(match as any, { emitEvent: false });
                this.categoryForm.get('category')?.disable({ emitEvent: false });
            }
        }

        this.categoryForm.get('category')?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe((value) => {
            if (this.disabled) return;

            if (typeof value === 'string' && value.trim()) {
                this.searchCategory(value);
                return;
            }

            if (value && typeof value === 'object') {
                this.onCategorySelected(value);
            }

            this.emitSelection();
        });

        this.categoryForm.get('serviceRequestCode')?.valueChanges.pipe(
            debounceTime(300),
            distinctUntilChanged()
        ).subscribe((value) => {
            if (this.disabled) return;

            if (typeof value === 'string') {
                // Allow searching even with empty string (to load all codes for selected category).
                this.searchServiceRequestCode(value);
                this.emitSelection();
                return;
            }

            if (value && typeof value === 'object') {
                this.emitSelection();
            }
        });

        // If we preselected a category from typeOfService, load its codes.
        if (this.selectedCategory) {
            this.searchServiceRequestCode('');
            this.emitSelection();
        }
    }

    onCategorySelected(category: any): void {
        this.selectedCategory = category?.code ?? null;

        this.serviceRequestOptions = [];
        this.filteredServiceRequestOptions = [];

        this.categoryForm.get('serviceRequestCode')?.setValue('', { emitEvent: false });

        // Load all codes for this category.
        this.searchServiceRequestCode('');
    }

    searchCategory(searchTerm: string): void {
        const term = (searchTerm || '').toLowerCase().trim();

        if (!term) {
            this.filteredCategoryOptions = [...this.categoryOptions];
            return;
        }

        this.filteredCategoryOptions = this.categoryOptions.filter((category) => {
            const display = (category.display || '').toLowerCase();
            const code = (category.code || '').toLowerCase();
            return display.includes(term) || code.includes(term);
        });
    }

    searchServiceRequestCode(searchTerm: string): void {
// $#$        // /* alert("i am here"); */
        if (this.requireCategory && !this.selectedCategory) {
            this.serviceRequestOptions = [];
            this.filteredServiceRequestOptions = [];
            this.loadingServiceRequests = false;
            return;
        }

        this.loadingServiceRequests = true;

        this.utilityService.searchServiceRequestCodeFromCodeSystem(searchTerm, this.selectedCategory).subscribe({
            next: (options) => {
// $#$                // /* alert('Loaded ' + options.length + ' service request codes for category '); */
                this.serviceRequestOptions = options;
                this.filteredServiceRequestOptions = options;
                // this.loadingServiceRequests = false;
            },
            complete: () => {
                this.loadingServiceRequests = false;
            },
            error: (err) => {
                console.error('Error searching service request codes:', err);
                this.loadingServiceRequests = false;
                this.errorService.openandCloseError('Failed to search service request codes');
            }
        });
    }

    displayCategory(option: any): string {
        return option?.display || '';
    }

    displayServiceCode(option: any): string {
        return option?.display || '';
    }

    private emitSelection(): void {
        const categoryValue: any = this.categoryForm.get('category')?.value;
        const serviceCodeValue: any = this.categoryForm.get('serviceRequestCode')?.value;

        const category: ServiceRequestCategoryOption | null =
            categoryValue && typeof categoryValue === 'object' && categoryValue.code
                ? { code: categoryValue.code, display: categoryValue.display, system: categoryValue.system }
                : null;

        const serviceRequestCode: ServiceRequestCodeOptionWithStatus | string | null =
            serviceCodeValue && typeof serviceCodeValue === 'object'
                ? serviceCodeValue
                : (serviceCodeValue ?? null);

        this.selectionChange.emit({ category, serviceRequestCode });
    }
}
